import React, { useEffect, useState } from "react";
import { useEnvironment } from "@/context/EnvironmentContext";
import { Button, Select, Switch, Modal, Segmented } from "antd";
import { useAuth } from "@/context/AuthContext";
import { DeleteOutlined, LogoutOutlined } from "@ant-design/icons";
import UserStyleSettings from "@/components/UserStyleSettings";
import CustomPropertiesList, { type CustomProperty } from "@/components/settings/CustomPropertiesList";
import { SettingsRow, SettingsSection } from "@/components/settings/SettingsLayout";
import Logger from "@/utility/logger";
import {
  getStoredRationaleEvidenceDetailMode,
  isRationaleEvidenceDetailMode,
  RATIONALE_EVIDENCE_DETAIL_CHANGED_EVENT,
  setStoredRationaleEvidenceDetailMode,
  type RationaleEvidenceDetailMode,
} from "@/utility/rationaleSettings";

interface SettingsPageProps {
  searchType: string;
  setSearchType: (value: string) => void;
  getStoredValue: (key: string) => Promise<any>;
  handleClearProperties: () => void;
  setShowError: (error: any) => void;
  titleMap: Record<string, string>;
  changeEnvironment: (envId: string) => void;
  logAuditAction: (id: string, action: "CREATE" | "UPDATE" | "DELETE", section_name?: string) => Promise<void>;
  reportTemplateProjectId: string | null;
  isSignedIn: boolean;
  onSignOut: () => void | Promise<void>;
}

const SettingsPage: React.FC<SettingsPageProps> = ({
  searchType,
  setSearchType,
  getStoredValue,
  handleClearProperties,
  setShowError,
  titleMap,
  changeEnvironment,
  logAuditAction,
  reportTemplateProjectId,
  isSignedIn,
  onSignOut,
}) => {
  const [customProperties, setCustomProperties] = useState<CustomProperty[]>([]);
  const [wordTaskEnabled, setWordTaskEnabled] = useState(true);
  const [rationaleEvidenceDetailMode, setRationaleEvidenceDetailMode] = useState<RationaleEvidenceDetailMode>(
    getStoredRationaleEvidenceDetailMode
  );

  const { environments, selectedEnvironment } = useEnvironment();
  const { isSuperAdmin } = useAuth();

  const searches = [
    { id: "hybrid", name: "Hybrid Search" },
    { id: "similarity", name: "Similarity Search" },
  ];

  const getCustomProperties = async () => {
    try {
      const properties = [];

      const orgId = await getStoredValue("selectedOrganizationId");
      if (orgId) {
        properties.push({ label: "Organization ID", value: orgId });
      }

      const projId = await getStoredValue("selectedProjectId");
      properties.push({ label: "Project ID", value: projId });

      const reportTemplateId = await getStoredValue("selectedReportTemplateId");
      properties.push({ label: "Report ID", value: reportTemplateId });

      const downloadId = await getStoredValue("jenlyReportDownloadId");
      if (downloadId) {
        properties.push({ label: "Download ID", value: downloadId });
      }

      const runId = await getStoredValue("jenlyReportRunId");
      if (runId) {
        properties.push({ label: "Report Run ID", value: runId });
      }

      const downloadVersion = await getStoredValue("jenlyDownloadSchemaVersion");
      if (downloadVersion) {
        properties.push({ label: "Download Schema Version", value: downloadVersion });
      }

      const genSects = await getStoredValue("generatedSections");
      properties.push({ label: "Generated Sections", value: genSects });

      const sectionStatus = await getStoredValue("sectionStatus");
      properties.push({ label: "Section Status", value: sectionStatus });

      const files = await getStoredValue("uploadedFiles");
      properties.push({ label: "Attached Files", value: files });

      return properties;
    } catch (error) {
      Logger.error("SettingsPage.tsx: Error fetching custom properties:", error);
      setShowError({
        isVisible: true,
        type: "error",
        title: titleMap[error.name],
        message: error.name,
        details: {
          message: error.message,
          code: error.code,
          stack: error.stack,
        },
      });
      return [];
    }
  };

  useEffect(() => {
    const fetchProperties = async () => {
      const props = await getCustomProperties();
      setCustomProperties(props);
    };

    try {
      if (
        typeof Office !== "undefined" &&
        Office.context &&
        Office.context.document &&
        Office.context.document.settings
      ) {
        const settingValue = Office.context.document.settings.get("JENLY_GENERATE_COMMENTS");

        if (settingValue === false || settingValue === "false") {
          setWordTaskEnabled(false);
        } else {
          setWordTaskEnabled(true);
        }
      }
    } catch (e) {
      Logger.error("SettingsPage.tsx: Error reading Word settings:", e);
    }

    fetchProperties();
  }, []);

  useEffect(() => {
    const syncRationaleEvidenceDetailMode = (event?: Event) => {
      const detail = event instanceof CustomEvent ? event.detail : null;
      setRationaleEvidenceDetailMode(
        isRationaleEvidenceDetailMode(detail) ? detail : getStoredRationaleEvidenceDetailMode()
      );
    };

    window.addEventListener(RATIONALE_EVIDENCE_DETAIL_CHANGED_EVENT, syncRationaleEvidenceDetailMode);
    return () => {
      window.removeEventListener(RATIONALE_EVIDENCE_DETAIL_CHANGED_EVENT, syncRationaleEvidenceDetailMode);
    };
  }, []);

  const handleWordTaskToggle = (checked: boolean) => {
    setWordTaskEnabled(checked);
    Logger.trace("SettingsPage.tsx: Settings changed: Word task toggle", {
      action: "settings_change",
      setting: "word_task_toggle",
      value: checked,
    });

    if (reportTemplateProjectId) {
      logAuditAction(reportTemplateProjectId, "UPDATE", `Word Task Toggle: ${checked ? "On" : "Off"}`);
    }

    try {
      if (
        typeof Office !== "undefined" &&
        Office.context &&
        Office.context.document &&
        Office.context.document.settings
      ) {
        Office.context.document.settings.set("JENLY_GENERATE_COMMENTS", checked);

        Office.context.document.settings.saveAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            Logger.error("SettingsPage.tsx: Failed to save settings: " + result.error.message);
          } else {
            Logger.debug("SettingsPage.tsx: Settings saved successfully. TODO task tracking status is ", checked);
          }
        });
      } else {
        Logger.warn("SettingsPage.tsx: Office.context.document.settings is not available");
      }
    } catch (e) {
      Logger.error("SettingsPage.tsx: Error saving Word settings:", e);
    }
  };

  const handleRationaleEvidenceDetailModeChange = (value: string | number) => {
    if (!isRationaleEvidenceDetailMode(value)) return;

    setRationaleEvidenceDetailMode(value);
    setStoredRationaleEvidenceDetailMode(value);
    Logger.trace("SettingsPage.tsx: Settings changed: Rationale evidence detail", {
      action: "settings_change",
      setting: "rationale_evidence_detail_mode",
      value,
    });

    if (reportTemplateProjectId) {
      logAuditAction(reportTemplateProjectId, "UPDATE", `Rationale Evidence Detail: ${value}`);
    }
  };

  const showDeveloperSettings = selectedEnvironment?.id !== "prod" || isSuperAdmin;

  return (
    <div className="settings_content taskpane-compact">
      <h1 className="settings_title ui-text-page-title">Settings</h1>

      <div className="settings_panel">
        {isSignedIn && (
          <SettingsSection title="Account">
            <SettingsRow label="Sign out" description="End your Jenly session.">
              <Button size="small" icon={<LogoutOutlined />} onClick={onSignOut}>
                Sign Out
              </Button>
            </SettingsRow>
          </SettingsSection>
        )}

        <UserStyleSettings logAuditAction={logAuditAction} reportTemplateProjectId={reportTemplateProjectId} />

        <SettingsSection title="Tasks">
          <SettingsRow label="Comment thread for tasks" description="Create a Word comment thread for each task.">
            <Switch checked={wordTaskEnabled} onChange={handleWordTaskToggle} aria-label="Comment thread for tasks" />
          </SettingsRow>
        </SettingsSection>

        {isSuperAdmin && (
          <SettingsSection title="Rationale">
            <SettingsRow
              label="Evidence detail"
              description="How much evidence to show with each rationale."
              layout="stacked"
            >
              <Segmented
                block
                size="small"
                className="settings_segmented_control"
                value={rationaleEvidenceDetailMode}
                onChange={handleRationaleEvidenceDetailModeChange}
                options={[
                  { label: "Less detail", value: "less" },
                  { label: "More detail", value: "more" },
                ]}
              />
            </SettingsRow>
          </SettingsSection>
        )}

        {showDeveloperSettings && (
          <SettingsSection title="Developer">
            <SettingsRow label="Environment">
              <Select
                className="settings_form_control"
                placeholder="Select environment"
                value={selectedEnvironment?.id}
                onChange={changeEnvironment}
                size="small"
              >
                {environments.map((env) => (
                  <Select.Option key={env.id} value={env.id}>
                    {env.name}
                  </Select.Option>
                ))}
              </Select>
            </SettingsRow>
            <SettingsRow label="Search type">
              <Select
                className="settings_form_control"
                placeholder="Select search type"
                value={searchType}
                onChange={(value) => setSearchType(value)}
                size="small"
              >
                {searches.map((search) => (
                  <Select.Option key={search.id} value={search.id}>
                    {search.name}
                  </Select.Option>
                ))}
              </Select>
            </SettingsRow>
            <SettingsRow label="Stored data" description="Clear locally stored settings and cached data.">
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() =>
                  Modal.confirm({
                    title: "Clear all stored data?",
                    content:
                      "This will clear all locally stored settings, properties, and cached data. This action cannot be undone.",
                    okText: "Clear",
                    okType: "danger",
                    onOk: handleClearProperties,
                  })
                }
              >
                Clear
              </Button>
            </SettingsRow>

            <div className="settings_group_heading ui-text-label">Custom properties</div>
            <CustomPropertiesList properties={customProperties} />
          </SettingsSection>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
